import type { Disposer } from './result.js';

type Handler<E> = (event: E) => void;

/** Read-only side of an emitter that UI code subscribes to. Every subscription returns its own disposer. */
export interface EventSource<E extends { readonly type: string }> {
  on<K extends E['type']>(type: K, handler: Handler<Extract<E, { type: K }>>): Disposer;
  onAny(handler: Handler<E>): Disposer;
}

/**
 * Small typed emitter. A throwing listener is isolated from the engine and from other
 * listeners; the error is passed to `onListenerError` instead of propagating into call handling.
 */
export class Emitter<E extends { readonly type: string }> implements EventSource<E> {
  private readonly typed = new Map<string, Set<Handler<never>>>();
  private readonly any = new Set<Handler<E>>();

  constructor(private readonly onListenerError: (error: unknown) => void = () => {}) {}

  on<K extends E['type']>(type: K, handler: Handler<Extract<E, { type: K }>>): Disposer {
    let set = this.typed.get(type);
    if (!set) { set = new Set(); this.typed.set(type, set); }
    const entry = handler as Handler<never>;
    set.add(entry);
    return () => { set.delete(entry); };
  }

  onAny(handler: Handler<E>): Disposer {
    this.any.add(handler);
    return () => { this.any.delete(handler); };
  }

  emit(event: E): void {
    for (const handler of [...(this.typed.get(event.type) ?? [])]) this.call(handler as Handler<E>, event);
    for (const handler of [...this.any]) this.call(handler, event);
  }

  get listenerCount(): number {
    let count = this.any.size;
    for (const set of this.typed.values()) count += set.size;
    return count;
  }

  clear(): void { this.typed.clear(); this.any.clear(); }

  private call(handler: Handler<E>, event: E): void {
    try { handler(event); } catch (error) { this.onListenerError(error); }
  }
}

/**
 * Snapshot store compatible with React `useSyncExternalStore`: `getSnapshot` returns the same
 * frozen object until something changes, and `subscribe` returns an unsubscribe function.
 */
export class SnapshotStore<T> {
  private readonly listeners = new Set<() => void>();
  private current: T | null = null;

  constructor(private readonly build: () => T, private readonly onListenerError: (error: unknown) => void = () => {}) {}

  getSnapshot = (): T => {
    if (this.current === null) this.current = this.build();
    return this.current;
  };

  subscribe = (listener: () => void): Disposer => {
    this.listeners.add(listener);
    return () => { this.listeners.delete(listener); };
  };

  invalidate(): void {
    this.current = null;
    for (const listener of [...this.listeners]) {
      try { listener(); } catch (error) { this.onListenerError(error); }
    }
  }

  get listenerCount(): number { return this.listeners.size; }

  clear(): void { this.listeners.clear(); }
}
